import { GRANDEZAS_DA_SECAO, colunaPrincipal, numeroBr } from '../../cptu/visual';
import type { GrandezaDaSecao, RespostaDaGrade } from '../../cptu/visual';

/**
 * A vista 3D para o relatório — onda 316, Tarefa 316.6 (a folha «Vista 3D» do PDF; R9 da 0257).
 *
 * A captura do `Modelo3d` (316.5) já sai no papel claro, com o norte, a legenda das cores, a frase do modelo
 * interpretativo, o exagero, a opacidade e o crédito do satélite. A coluna da campanha guarda só o arquivo
 * (`vista3d_arquivo_id`), e a folha do PDF precisa dizer o método e os parâmetros do variograma: eles vão ESCRITOS na
 * imagem, numa faixa embaixo dela — a grandeza, o método, o variograma de cada coluna e a grade, como a tela os mostra.
 * As cores são as do papel claro (a regra `:root` dos tokens), nunca escritas à mão.
 */

/** O tamanho da captura, px: a proporção da área da imagem na folha deitada (273 × 128 mm). */
export const TAMANHO_DA_CAPTURA = { largura: 2400, altura: 1125 } as const;

const NOME_DA_COLUNA: Record<string, string> = { logFr: 'log₁₀ Fr', logQtn: 'log₁₀ Qtn' };

/** As linhas do método da vista: a grandeza, o método com o variograma de cada coluna (ou o porquê do IDW) e a grade. */
export function linhasDoMetodo(g: Pick<RespostaDaGrade, 'variogramas' | 'metodoUsado' | 'motivoDoMetodo' | 'grade'>, grandeza: GrandezaDaSecao): string[] {
  const def = GRANDEZAS_DA_SECAO.find((x) => x.chave === grandeza);
  const principal = colunaPrincipal(grandeza);
  const nome = (c: string) => NOME_DA_COLUNA[c] ?? GRANDEZAS_DA_SECAO.find((x) => x.chave === c)?.rotulo ?? c;
  const metodo = g.metodoUsado === 'krigagem'
    ? `Método: krigagem ordinária (${g.variogramas[0]?.ajustado.modelo === 'exponencial' ? 'exponencial' : 'esférico'}) — `
      + g.variogramas.map((v) => `${nome(v.coluna)}${v.coluna === principal && g.variogramas.length > 1 ? ' (a principal)' : ''}: pepita `
        + `${numeroBr(v.ajustado.pepita, 3)} · patamar ${numeroBr(v.ajustado.patamar, 3)} · alcance ${numeroBr(v.ajustado.alcance, 2)} m`).join('; ')
    : `Método: IDW (potência 2)${g.motivoDoMetodo ? ` — ${g.motivoDoMetodo}` : ''}`;
  const p = g.grade.passo;
  return [
    `Grandeza do modelo: ${def?.rotulo ?? grandeza}${def?.unidade ? ` (${def.unidade})` : ''}`,
    metodo,
    `Grade ${numeroBr(p.x, 0)} × ${numeroBr(p.y, 0)} × ${numeroBr(p.z, 2)} m — escolhas da tela, sem fonte`,
  ];
}

/** As cores do papel claro: a regra `:root` exata das folhas de estilo (o tema claro); sem ela, o valor computado. */
function coresDoPapelClaro(): { papel: string; tinta: string } {
  const lidas: Record<string, string> = {};
  const visitar = (regras: CSSRuleList): void => {
    for (const r of Array.from(regras)) {
      if (typeof CSSImportRule !== 'undefined' && r instanceof CSSImportRule) {
        try { if (r.styleSheet) visitar(r.styleSheet.cssRules); } catch { /* folha de outra origem */ }
        continue;
      }
      if (!(r instanceof CSSStyleRule) || r.selectorText.trim() !== ':root') continue;
      for (const n of ['--ts-papel', '--ts-tinta']) {
        const v = r.style.getPropertyValue(n).trim();
        if (v && !v.startsWith('var(') && !lidas[n]) lidas[n] = v;
      }
    }
  };
  for (const folha of Array.from(document.styleSheets)) {
    try { visitar(folha.cssRules); } catch { /* folha de outra origem */ }
  }
  const raiz = getComputedStyle(document.documentElement);
  return {
    papel: lidas['--ts-papel'] ?? raiz.getPropertyValue('--ts-papel').trim(),
    tinta: lidas['--ts-tinta'] ?? raiz.getPropertyValue('--ts-tinta').trim(),
  };
}

/** A captura com a faixa do método embaixo, em PNG. */
export async function comporAVista(captura: Blob, linhas: readonly string[]): Promise<Blob> {
  const imagem = await createImageBitmap(captura);
  const k = Math.max(1, imagem.height / 675);
  const fonte = `${Math.round(14 * k)}px ${getComputedStyle(document.body).fontFamily}`;
  const medir = document.createElement('canvas').getContext('2d');
  if (!medir) throw new Error('sem canvas 2D para compor a vista');
  medir.font = fonte;
  // As linhas longas quebram pelas palavras, na largura da imagem.
  const largura = imagem.width - 24 * k;
  const quebradas: string[] = [];
  for (const linha of linhas) {
    let atual = '';
    for (const palavra of linha.split(' ')) {
      const teste = atual ? `${atual} ${palavra}` : palavra;
      if (atual && medir.measureText(teste).width > largura) { quebradas.push(atual); atual = palavra; } else atual = teste;
    }
    if (atual) quebradas.push(atual);
  }
  const altura = Math.round(imagem.height + (quebradas.length * 20 + 16) * k);
  const canvas = document.createElement('canvas');
  canvas.width = imagem.width;
  canvas.height = altura;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('sem canvas 2D para compor a vista');
  const cor = coresDoPapelClaro();
  ctx.fillStyle = cor.papel;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(imagem, 0, 0);
  ctx.strokeStyle = cor.tinta;
  ctx.lineWidth = Math.max(1, k);
  ctx.beginPath(); ctx.moveTo(0, imagem.height + 0.5); ctx.lineTo(canvas.width, imagem.height + 0.5); ctx.stroke();
  ctx.fillStyle = cor.tinta;
  ctx.font = fonte;
  ctx.textBaseline = 'top';
  quebradas.forEach((t, i) => ctx.fillText(t, 12 * k, imagem.height + (8 + i * 20) * k));
  imagem.close();
  return new Promise<Blob>((ok, falha) => canvas.toBlob((b) => (b ? ok(b) : falha(new Error('a vista não virou imagem'))), 'image/png'));
}
